import { Avatar, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Champ, UserDetail } from '../../types';
import React, { useEffect, useRef, useState } from 'react';
import { USER_BY_IDS_API } from '../../api/endpoint';
import RenderProfileImage from '../../components/profileImageBlur';
import Box from '@mui/material/Box';
import axios from 'axios';
import createAxiosInstance from '../../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

const StarChamps = () => {
  const [champs, setChamps] = useState<Champ[]>([]);
  const [userDetails, setUserDetails] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const isFetching = useRef(false);
  const [activeSlide, setActiveSlide] = useState(2);
  const [stopExecution, setStopExecution] = useState<boolean | null>(false);
  const scrollRef = useRef(null);
  const scrollSpeed = 50;
  const navigate = useNavigate();

  const isMobile = useMediaQuery('(max-width:600px)');

  const imageWidth = isMobile ? 100 : 302;
  const imageHeight = isMobile ? 100 : 302;

  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    delta: 10,
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const fetchChamps = async (pageToken: string | null = null) => {
    if (!stopExecution) {
      if (isFetching.current) return;
      isFetching.current = true;

      try {
        const axiosInstance = createAxiosInstance(true);
        const response = await axiosInstance.get(`/APP/Home/TopUsers.json`, {
          params: { page: pageToken || null, count: 5 }
        });
        if (!response?.data?.data?.length) {
          setStopExecution(true);
        }
        setChamps((prevChamps) => [...prevChamps, ...response.data.data]);
        setCurrentPage(response.data.page);

        const userIds = response.data.data.map((champ: Champ) => champ.user_id);
        if (userIds.length > 0) {
          const detailsAxios = createAxiosInstance(false);
          const userDetailsResponse = await detailsAxios.get(`${USER_BY_IDS_API}${userIds.join(',')}`);

          setUserDetails((prevDetails) => {
            const existingIds = new Set(prevDetails.map((user) => user.uid));
            const newDetails = userDetailsResponse.data.data.filter((user: UserDetail) => !existingIds.has(user.uid));

            const allUsers = newDetails.map((userDetail: UserDetail) => {
              if (userDetail?.defaultAvatar) {
                const slideNum = Math.floor(Math.random() * 8) + 1;
                userDetail.defaultAvatarPath = `${process.env.REACT_APP_DEFAULT_AVATAR}/APP/UserAvatars/Avatar${slideNum}.png`;
              }
              return userDetail;
            });

            return [...prevDetails, ...allUsers];
          });
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setError(error.message);
        }
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    }
  };

  useEffect(() => {
    const scrollContainer: any = scrollRef.current;
    if (!scrollContainer) return;

    let interval = setInterval(() => {
      if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
        scrollContainer.scrollLeft = 0;
      } else {
        scrollContainer.scrollLeft += 1;
      }
    }, scrollSpeed);

    const handleMouseEnter = () => clearInterval(interval);
    const handleMouseLeave = () => {
      interval = setInterval(() => {
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
          scrollContainer.scrollLeft = 0;
        } else {
          scrollContainer.scrollLeft += 1;
        }
      }, scrollSpeed);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearInterval(interval);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [userDetails]);

  useEffect(() => {
    fetchChamps();
  }, []);

  useEffect(() => {
    if (activeSlide === sliderData.length - 1) {
      fetchChamps(currentPage);
    }
  }, [activeSlide, currentPage]);

  const sliderData = champs.map((champ, index) => {
    const userDetail = userDetails.find((user, index: number) => user.uid === champ.user_id);
    return {
      id: champ.user_id + index,
      bgColor: 'transparent',
      icon: (
        <Avatar
          key={index}
          alt={userDetail?.handle}
          src={
            userDetail?.defaultAvatar
              ? userDetail?.defaultAvatarPath
              : `${process.env.REACT_APP_DEFAULT_AVATAR}/${userDetail?.uid}/PROFILE/IMAGES/medium/${userDetail?.avatar}`
          }
          onClick={() => navigate(`/user/${userDetail?.handle}`)}
          sx={{
            width: imageWidth,
            height: imageHeight,
            m: isMobile ? 'auto' : 'inherit',
            backgroundColor: '#bdbdbd'
          }}
          className="slide-img-circular"
        />
      ),
      title: userDetail?.firstname || 'Anonymous'
    };
  });

  const next = () => {
    if (activeSlide < sliderData.length - 1) {
      setActiveSlide(activeSlide + 1);
    }
  };

  const prev = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
    }
  };

  const goToSlide = (index: number) => {
    setActiveSlide(index);
  };

  const getStyles = (index: number) => {
    const screenWidth = window.innerWidth;
    const baseTranslateX = isMobile ? screenWidth * 0.15 : screenWidth * 0.17;
    const baseTranslateZ = isMobile ? screenWidth * 0.3 : screenWidth * 0.2;

    if (activeSlide === index) {
      return {
        opacity: 1,
        transform: 'translateX(0px) translateZ(0px) rotateY(0deg)',
        zIndex: 10
      };
    } else if (activeSlide - 1 === index) {
      return {
        opacity: 1,
        transform: `translateX(-${baseTranslateX}px) translateZ(-${baseTranslateZ}px) rotateY(35deg)`,
        zIndex: 9
      };
    } else if (activeSlide + 1 === index) {
      return {
        opacity: 1,
        transform: `translateX(${baseTranslateX}px) translateZ(-${baseTranslateZ}px) rotateY(-35deg)`,
        zIndex: 9
      };
    } else if (activeSlide - 2 === index) {
      return {
        opacity: 1,
        transform: `translateX(-${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(35deg)`,
        zIndex: 8
      };
    } else if (activeSlide + 2 === index) {
      return {
        opacity: 1,
        transform: `translateX(${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(-35deg)`,
        zIndex: 8
      };
    } else if (index < activeSlide - 2) {
      return {
        opacity: 0,
        transform: `translateX(-${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(35deg)`,
        zIndex: 7
      };
    } else if (index > activeSlide + 2) {
      return {
        opacity: 0,
        transform: `translateX(${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(-35deg)`,
        zIndex: 7
      };
    }
  };

  if (error) {
    return (
      <Typography variant="h6" color="error">
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: isMobile ? '4rem 0 3rem 0' : '5rem  0'
      }}>
      <Typography
        style={{
          fontSize: isMobile ? '24px' : '52px',
          fontFamily: 'Baloo2',
          color: ' rgba(255, 255, 255, 1)',
          display: 'inline-block',
          textAlign: 'center',
          fontWeight: 800
        }}>
        Star Champs
      </Typography>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: '30px',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          width: '100%',
          padding: '16px'
        }}>
        {userDetails.map((item: any, index: number) => (
          <Box
            key={index}
            sx={{ alignItems: 'center', padding: '20px 0 0 10px ', cursor: 'pointer' }}
            onClick={() => navigate(`/user/${item?.handle}`)}>
            <Box sx={{ border: '4px solid white', borderRadius: '50%' }}>
              {/* <Avatar
                sx={{
                  width: isMobile ? '100px' : '200px',
                  height: isMobile ? '100px' : '200px',
                  filter: item.defaultAvatar ? 'none' : 'blur(8px)'
                }}
                src={
                  item.defaultAvatar
                    ? item.defaultAvatarPath
                    : `${process.env.REACT_APP_DEFAULT_AVATAR}/${item?.uid}/PROFILE/IMAGES/medium/${item?.avatar}`
                }></Avatar> */}
              <RenderProfileImage
                imgHeight={isMobile ? '100px' : '200px'}
                imgWidth={isMobile ? '100px' : '200px'}
                defaultAvatar={item.defaultAvatar}
                defaultAvatarPath={item.defaultAvatarPath}
                uid={item?.uid}
                avatar={item?.avatar}
              />
            </Box>
            <Typography
              sx={{
                textAlign: 'center',
                padding: '10px',
                fontSize: isMobile ? '14px' : '29px',
                color: 'white',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? '100px' : '200px'
              }}>
              {item.firstname}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StarChamps;
